import { Fragment } from "react/jsx-runtime";
import { AbccAssessmentModule } from "../Models/CCAssessment";
import { ExaminationGrade } from "../../../Examination/Enums/ExaminationGrade";
import { Button, Col, Form, ProgressBar, Row } from "react-bootstrap";
import { getDateWithOffset } from "../../../Shared/Utility/GetDateWithOffset";
import { useEffect, useState } from "react";
import { configService } from "../../../../Services/Config/ConfigService";
import { ArticleResultList } from "./ArticleResultList";
import { CCArticleWaiver } from "../../../Physician/Models/CCBlock";

interface IProps{
    abccAssessmentModule: AbccAssessmentModule;
    acceptAttestation: (abccAssessmentModule: AbccAssessmentModule) => void;
    appliedArticleWaivers: Array<CCArticleWaiver>;
    ccArticlePassCountFromPreviousModule: number; //Set to 0 for non-catchup modules
    ccArticleFailedCountFromPreviousModule: number; //Set to 0 for non-catchup modules
}

export default function CCAbccAssessmentModuleSummary(props: IProps){
    const abccAssessmentModuleStartDate = getDateWithOffset(props.abccAssessmentModule.startDate);
    const abccAssessmentModuleEndDate = getDateWithOffset(props.abccAssessmentModule.endDate);
    const [detailVisible, setDetailVisible] = useState<boolean>(false);
    const [articleAccessUrl, setArticleAccessUrl] = useState<string>(null);
    const [attestationIsAccepted, setAttestationIsAccepted] = useState<boolean>(null);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    
    useEffect(() => {
        const getArticleAccessUrl = async () => {
            let cfg = (await configService.getConfiguration()).ArticleAccessUrl;
            setArticleAccessUrl(cfg);
        }
        getArticleAccessUrl();
    }, []);
    
    const getGradeDescription = (predictedToFail: boolean) => {
        switch (props.abccAssessmentModule.grade){
            case ExaminationGrade.Pass:
                return "Pass";
            case ExaminationGrade.Fail:
                return "Failed";
            case ExaminationGrade.NoGrade:
            default: 
                return predictedToFail ? "Failed" : "In Progress";
            
        }
    };

    const displayAbbreviatedSummary = (predictedToFail: boolean) => {
        const moduleStyle = () => {
            switch(props.abccAssessmentModule.grade){
                case ExaminationGrade.Pass:
                    return "bg-pp-success passed";
                case ExaminationGrade.Fail:
                    return "bg-pp-danger failed";
                default:
                    return predictedToFail ? "bg-pp-danger failed" : "";
            }
        }
        
        return (
            <Row className={`ccassessment-module ${moduleStyle()}`}>
                <Col>
                    <div className="font-weight-bold">
                        {abccAssessmentModuleStartDate.getFullYear()} - {abccAssessmentModuleEndDate.getFullYear()}
                    </div>
                </Col>
                <Col>
                    <div className="font-weight-bold">
                        Result: {getGradeDescription(predictedToFail)}
                    </div>
                </Col>
                <Col>
                    <div className="font-weight-bold">
                        As of: {abccAssessmentModuleEndDate.toLocaleDateString('en-US')}
                    </div>
                </Col>
            </Row>
        )
    };

    const displayUpcomingSummary = () => {
        return (
            <Row className="ccassessment-module upcoming">
                <Col>
                    <div>
                        {abccAssessmentModuleStartDate.getFullYear()} - {abccAssessmentModuleEndDate.getFullYear()}
                    </div>
                </Col>
                <Col>
                    <div>
                        Upcoming
                    </div>
                </Col>
                <Col>
                    <div>
                        &nbsp;
                    </div>
                </Col>
            </Row>
        )
    };

    const getArticlesWaived = () => {
        if(!props.appliedArticleWaivers) return 0;
        const articleSum = props.appliedArticleWaivers?.reduce((sum, current) => sum + current.articleCredits, 0);
        return articleSum;
    }

    const getArticlesPassed = () => {
        if (!props.abccAssessmentModule.ccArticleResults) return 0;
        return props.abccAssessmentModule.ccArticleResults.filter(_ => _.isPass).length + props.ccArticlePassCountFromPreviousModule;
    }

    const getArticlesFailed = () => {
        if (!props.abccAssessmentModule.ccArticleResults) return 0;
        return props.abccAssessmentModule.ccArticleResults.filter(_ => !_.isPass).length + props.ccArticleFailedCountFromPreviousModule;
    }

    const getArticlesRemaining = () => {
        var numRemaining = props.abccAssessmentModule.requiredArticlesPerModule - getArticlesPassed() - getArticlesWaived();
        return numRemaining < 0 ? 0 : numRemaining;
    }

    const predictedToFail = () => {
        return getArticlesFailed() > props.abccAssessmentModule.maxFailedArticlesAllowedPerModule;
    }

    const displayFullSummary = () => {
        return (
            <Fragment>
                <Row className={`ccassessment-module ${props.abccAssessmentModule.isCatchUp ? "catchup" : ""}`}>
                    <Col>
                        <Row className="ccassessment-module-active">
                            <Col>
                                <div className="font-weight-bold">
                                    {abccAssessmentModuleStartDate.getFullYear()} - {abccAssessmentModuleEndDate.getFullYear()}
                                </div>
                            </Col>
                            <Col>
                                <div className="font-weight-bold">
                                    Result: {getGradeDescription(predictedToFail())}
                                </div>
                            </Col>
                            <Col>
                                <div className="font-weight-bold">
                                    <div>Due By: {abccAssessmentModuleEndDate.toLocaleDateString('en-US')}</div>
                                    <div><small>Article Exams for this cycle <br />must be completed by:&nbsp;<strong>{abccAssessmentModuleEndDate.toLocaleDateString('en-US')}</strong></small></div>
                                    <div>
                                        <Form.Group controlId="articleProgress">
                                            <div className="d-flex flex-row ">
                                                <div className="w-75 mb-1 mt-1">
                                                    <ProgressBar variant="pp-secondary" role="progressbar" now={getArticlesPassed() + getArticlesWaived()} max={props.abccAssessmentModule.requiredArticlesPerModule} ></ProgressBar>
                                                </div>
                                                <Form.Text className="ml-1">{getArticlesPassed() + getArticlesWaived()}/{props.abccAssessmentModule.requiredArticlesPerModule}</Form.Text>        
                                            </div>                  
                                        </Form.Group>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <hr />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Row>
                                    <Col className="ccassessment-abcc-module-articlesremaining bold">{getArticlesRemaining()}</Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <div>Article Exams Remaining</div>
                                        {props.abccAssessmentModule.isCatchUp && <div><small><b>Note: The number of 
                                            required article exams in this catch up cycle reflects <i>both</i> the required 
                                            article exams from the previous, unsuccessful cycle, in addition to the current cycle's requirement.</b></small></div>}
                                    </Col>
                                </Row>
                            </Col>
                            <Col>
                                <Row>
                                    <Col><small>{getArticlesPassed() + getArticlesWaived()} of {props.abccAssessmentModule.requiredArticlesPerModule} Complete</small></Col>
                                </Row>
                                <Row>
                                    <Col><small>{getArticlesFailed()} of {props.abccAssessmentModule.maxFailedArticlesAllowedPerModule} Failed</small></Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <small>
                                            {(props.abccAssessmentModule.requiredArticlesPerModule + props.abccAssessmentModule.maxFailedArticlesAllowedPerModule) - (getArticlesPassed() + getArticlesWaived() + getArticlesFailed())} Article Exam Attempts Remain
                                        </small>
                                    </Col>
                                </Row>
                            </Col>
                            <Col>
                                <Row>
                                    <Col>
                                        <div>
                                            { <a href={articleAccessUrl} target="_blank"><Button variant="pp-primary" className="mb-3 mr-1">Access Article Exams<i className="ml-1 las la-external-link-alt"></i></Button></a>}
                                            { <Button className="mb-3" variant="outline-pp-primary" onClick={() => setDetailVisible(!detailVisible)}>{detailVisible ? "Hide" : "Show"} Progress Detail</Button>}
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <small><strong>One <a href="https://abpn.org/wp-content/uploads/2025/02/ABCC-FAQ_2025.pdf">Professionalism article exam</a> is required every 3-year ABCC cycle.</strong></small>
                            </Col>
                        </Row>
                        { detailVisible &&
                            <Row>
                                <Col className="ccassessment-module-ccarticle-results">
                                    <ArticleResultList articleResults={props.abccAssessmentModule.ccArticleResults}/>
                                </Col>
                            </Row>
                        }
                    </Col>
                </Row>
            </Fragment>
        )
    };

    const acceptAttestation = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAttestationIsAccepted(e.target.checked);
        setIsSubmitDisabled(false);
    }

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsSubmitDisabled(true);
    
            // todo validate all attested
            if (attestationIsAccepted){
                props.acceptAttestation(props.abccAssessmentModule);
            }
    
            setIsSubmitDisabled(false);
        }

    const displayAttestation = () => {
        return (
            <Row className="ccassessment-module attestation">
                <Col>
                    <Row className="font-weight-bold">
                        <Col>
                                <div className="font-weight-bold">Article-Based Continuing Certification (ABCC) Pathway Attestation</div>
                                <div>{abccAssessmentModuleStartDate.getFullYear()} - {abccAssessmentModuleEndDate.getFullYear()}</div>
                            </Col>
                    </Row>
                    <Row>
                        <Col>Please review an accept the terms below:</Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form noValidate onSubmit={(e) => onSubmit(e)}>
                                <Form.Group>
                                    <Form.Text className="font-weight-bold">Article-Based Continuing Certification (ABCC) Pathway Attestation</Form.Text>
                                </Form.Group>
                                <Form.Group>
                                    <div className="pp-assessment-terms-body">
                                        <p>
                                            I attest that I have chosen the ABCC pathway to satisfy the Continuing Certification (CC) recertification exam requirement. 
                                            I agree that to meet the CC recertification exam requirement via the ABCC pathway, I must successfully complete a set of article 
                                            exams for each of my ABPN certificates on an ongoing basis for three consecutive 3-year blocks.  I agree to select and pass at least one article 
                                            examination designated in the ‘Professionalism’ area in each three-year cycle. I agree that to maintain my ABPN certification(s) I must also successfully 
                                            complete the CC Activity Requirements, pay an annual fee, and maintain a valid and unrestricted medical license. (For an overview 
                                            see <a href="https://abpn.org/continuing-certification/">https://abpn.org/continuing-certification/</a>.) 
                                            I agree that if I fail to successfully complete three (3) three-year ABCC blocks by the end of year 9, I understand I will be automatically transferred to the 
                                            Recertification Exam pathway and will need to successfully pass the Recertification Exam by the end of my 10-year recertification block.
                                        </p>

                                        <p>
                                            Qualifications for ABCC pathway accommodations for applicants with disabilities can be found on the ABPN website policies 
                                            section: <a href="https://abpn.org/wp-content/uploads/2024/01/ABCC-Applicants-with-Disabilities.pdf">https://abpn.org/wp-content/uploads/2024/01/ABCC-Applicants-with-Disabilities.pdf</a>. 
                                            Please note, however, that applications for accommodations must be submitted by February 1 of every 3-year block. I understand that requests for 
                                            changes in the duration of a 3-year block, extensions to a 3-year block, the number of article exams needing to be successfully 
                                            completed, or any other alteration in the grading or assessment will be deemed to fundamentally alter the measurement of the 
                                            skills or knowledge being assessed and will not be granted by the ABPN.
                                        </p>

                                        <p>
                                            I agree the Board is the final judge of my qualification for holding ABPN certification(s) and that I must adhere to the 
                                            Board's applicable policies (see <a href="https://abpn.org/about/policies/">https://abpn.org/about/policies/</a>).</p>

                                        <p>
                                            I agree to fully adhere to the Board's policy on Irregular Behavior, Including Unethical Behavior of Candidates 
                                            (<a href="https://www.abpn.org/wp-content/uploads/2022/07/Policy-Regarding-Irregular-Behavior.pdf">https://www.abpn.org/wp-content/uploads/2022/07/Policy-Regarding-Irregular-Behavior.pdf</a>), 
                                            and that I will not share questions or answers included in the ABCC examinations. I agree that I will not choose ABCC journal articles
                                             for which I was an author or contributor. Irregular or improper behaviors, as evidenced by observation, by subsequent statistical analysis, or by 
                                            other means, may be cause for the Board to terminate my examination, invalidate my examination results, bar me from admission
                                            to future examinations or certification, and to take other appropriate actions.
                                        </p>

                                        <p>
                                            I understand that the Board makes academic and scientific judgments in its evaluation of the results of its examinations, and that 
                                            situations may occur, even through no fault of mine, that will render my examination results unreliable in the judgement of the Board.
                                            I agree that if the Board determines that the results of my examination are unreliable, the Board may require me to retake that examination.
                                        </p>

                                        <p>
                                            See the website for additional information about the Article Based Continuing Certification (ABCC) pathway here: 
                                            <a href="https://abpn.org/continuing-certification/article-based-continuing-certification-pathway-abcc/">https://abpn.org/continuing-certification/article-based-continuing-certification-pathway-abcc/</a>
                                        </p>

                                        <p>
                                            See the General Policies regarding taking a CC / recertification examination here: <a href="https://abpn.org/continuing-certification/continuing-certification-exams/">https://abpn.org/continuing-certification/continuing-certification-exams/</a>
                                        </p>
                                    </div>
                                </Form.Group>
                                <Form.Group controlId="articlesFormAttestationControls">
                                    <Form.Check type="checkbox">
                                        <Form.Check.Input defaultChecked={false}  type="checkbox" required onChange={acceptAttestation} />
                                        <Form.Check.Label><strong>I hereby declare under penalty of perjury I agree to the terms and conditions of the Article Based Continuing Certification (ABCC) Pathway. I intend to be legally bound by the foregoing.</strong></Form.Check.Label>
                                        <Form.Control.Feedback type="invalid">Please attest to the above before continuing</Form.Control.Feedback>
                                    </Form.Check>
                                </Form.Group>
                                <Row>
                                    <Col xs={12} lg={6}>
                                        <Button disabled={isSubmitDisabled} type="submit" variant="pp-primary">Accept Terms</Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Col>
                    </Row>
                </Col>
            </Row>
        )
    };

    const displayAbccModule = () => {
        if (props.abccAssessmentModule.isCurrent() && props.abccAssessmentModule.attestationDate == null)
        {
            return displayAttestation();
        }
        else if (props.abccAssessmentModule.grade == ExaminationGrade.NoGrade && !predictedToFail() && getDateWithOffset(props.abccAssessmentModule.startDate) < new Date()){
            // This is still active, show full detail
            return displayFullSummary()
        }
        else if (props.abccAssessmentModule.grade == ExaminationGrade.NoGrade && getDateWithOffset(props.abccAssessmentModule.startDate) > new Date()){
            // The module hasn't started yet
            return displayUpcomingSummary()
        }
        else{
            return displayAbbreviatedSummary(predictedToFail())
        }
    };
    
    return (<Fragment>
        <Row>
            <Col>{displayAbccModule()}</Col>
        </Row>
    </Fragment>);
}